import { z } from 'zod';

import { request } from '@/api/httpClient';

const stopSchema = z.object({
  id: z.number().int().positive(),
  service_location: z.object({
    id: z.number().int().positive(),
    name: z.string(),
    address: z
      .object({
        street_number: z.string().nullable().optional(),
        route: z.string().nullable().optional(),
        locality: z.string().nullable().optional(),
        postal_code: z.string().nullable().optional(),
        latitude: z.union([z.string(), z.number()]).nullable().optional(),
        longitude: z.union([z.string(), z.number()]).nullable().optional(),
      })
      .nullable()
      .optional(),
    client: z.object({ id: z.number().int().positive(), name: z.string() }),
  }),
  cancellation: z.unknown().nullable().optional(),
  service_report_completed: z.boolean().nullable().optional(),
  weighting: z.coerce.number().nullable().optional(),
});
export type RouteStop = z.infer<typeof stopSchema>;
export async function getRouteStopsForRange(
  userId: number,
  startDate: string,
  endDate: string,
): Promise<Record<string, RouteStop[]>> {
  const value = await request<unknown>('/managers/get-route-stops-for-date-range', {
    method: 'POST',
    body: { user_id: userId, start_date: startDate, end_date: endDate },
  });
  return z.record(z.string(), z.array(stopSchema)).parse(value);
}

const technicianDaySchema = z.object({
  technician: z.object({
    id: z.number().int().positive(),
    name: z.string(),
    email: z.string().optional(),
  }),
  route_stops: z.array(stopSchema),
  route_stops_count: z.coerce.number().int().nonnegative(),
});
export type TechnicianDay = z.infer<typeof technicianDaySchema>;
export async function getAllTechnicianStops(date: string): Promise<TechnicianDay[]> {
  return z.array(technicianDaySchema).parse(
    await request<unknown>('/managers/get-route-stops-for-all-technicians', {
      method: 'POST',
      body: { date },
    }),
  );
}

export interface MoveRouteStopInput {
  routeAssignmentId: number;
  date: string;
  newUserId: number;
  newDate: string;
}

export async function moveRouteStop(value: MoveRouteStopInput): Promise<void> {
  await request('/managers/route-stop-alterations', {
    method: 'POST',
    body: {
      route_assignment_id: value.routeAssignmentId,
      date: value.date,
      new_user_id: value.newUserId,
      new_date: value.newDate,
    },
  });
}

export interface CancelRouteStopInput {
  routeAssignmentId: number;
  date: string;
  reason: string;
  userId: number;
}

export async function cancelRouteStop(value: CancelRouteStopInput): Promise<void> {
  await request('/managers/cancel-route-stop', {
    method: 'POST',
    body: {
      route_assignment_id: value.routeAssignmentId,
      date: value.date,
      reason: value.reason,
      user_id: value.userId,
    },
  });
}

export async function saveRouteWeighting(
  routeAssignmentId: number,
  date: string,
  userId: number,
  weight: number,
): Promise<void> {
  await request('/managers/route-weightings', {
    method: 'POST',
    body: { route_assignment_id: routeAssignmentId, date, user_id: userId, weight },
  });
}

const routeStopNoteSchema = z.object({
  id: z.number().int().positive(),
  note: z.string(),
});

export async function getRouteStopNote(
  routeAssignmentId: number,
  date: string,
  userId: number,
): Promise<string> {
  const notes = z.array(routeStopNoteSchema).parse(
    await request<unknown>('/managers/route-stop-notes/get-route-stop-notes', {
      method: 'POST',
      body: { route_assignment_id: routeAssignmentId, date, user_id: userId },
    }),
  );
  return notes[0]?.note ?? '';
}

export async function saveRouteStopNote(
  routeAssignmentId: number,
  date: string,
  userId: number,
  note: string,
): Promise<void> {
  await request('/managers/route-stop-notes/create-route-stop-note', {
    method: 'POST',
    body: { route_assignment_id: routeAssignmentId, date, user_id: userId, note },
  });
}

const technicianLocationSchema = z.object({
  latitude: z.union([z.string(), z.number()]).nullable(),
  longitude: z.union([z.string(), z.number()]).nullable(),
  location_last_updated: z.string().nullable(),
});
export type TechnicianLocation = z.infer<typeof technicianLocationSchema>;
export async function getTechnicianLocation(technicianId: number): Promise<TechnicianLocation> {
  return technicianLocationSchema.parse(
    await request<unknown>('/managers/get-technician-location', {
      method: 'POST',
      body: { technician_id: technicianId },
    }),
  );
}

const managementNoteSchema = z.object({
  technician: z.string(),
  service_location: z.object({
    id: z.number().int().positive(),
    name: z.string(),
    client_id: z.number().int().positive().optional(),
  }),
  notes_for_management: z.string(),
});
export type ManagementNote = z.infer<typeof managementNoteSchema>;
export async function getManagementNotes(date: string): Promise<ManagementNote[]> {
  return z.array(managementNoteSchema).parse(
    await request<unknown>('/managers/get-notes-for-management', {
      method: 'POST',
      body: { date },
    }),
  );
}
